import { Currency, CurrencyAmount, TradeType } from "@pancakeswap/swap-sdk-core";
import { Modal, useToast } from "@pancakeswap/uikit";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatEther, parseEther } from "viem";
import TransactionConfirmationModal from "components/TransactionConfirmationModal";
import { usePublicClient } from "wagmi";
import useAccountActiveChain from "hooks/useAccountActiveChain";
import { useUserSplitRouteEnable } from "state/user/smartRouter";
import { useUserQuickBuyAmount, useUserSingleHopOnly } from "@pancakeswap/utils/user";
import useTransactionDeadline from "hooks/useTransactionDeadline";
import useNativeCurrency from "hooks/useNativeCurrency";
import { useTranslation } from "@pancakeswap/localization";
import { useBestAMMTrade } from "hooks/useBestAMMTrade";
import { ConfirmationPendingContent } from "@pancakeswap/widgets-internal";
import { useSwapCallback } from "../V3Swap/hooks";

export function QuickSwapModal({ currency, onDismiss }: { currency: Currency, onDismiss?: () => void }) {
    const { t } = useTranslation()

    const native = useNativeCurrency()
    const [quickBuyAmount] = useUserQuickBuyAmount()
    const deadline = useTransactionDeadline()

    const [singleHopOnly] = useUserSingleHopOnly()
    const [split] = useUserSplitRouteEnable()
    const attempted = useRef(false)

    const { account } = useAccountActiveChain()
    // const nativeBalance = useCurrencyBalance(account ?? undefined, native ?? undefined)

    const publicClient = usePublicClient()

    const { isLoading, trade, error } = useBestAMMTrade({
        amount: CurrencyAmount.fromRawAmount(native, parseEther(String(quickBuyAmount))),
        currency,
        baseCurrency: native,
        tradeType: TradeType.EXACT_INPUT,
        maxHops: singleHopOnly ? 1 : undefined,
        maxSplits: split ? undefined : 0,
        v2Swap: false,
        v3Swap: true,
        stableSwap: true,
        type: 'auto',
    })

    const {
        callback: swapCallback,
        error: swapCallbackError,
        reason: revertReason,
    } = useSwapCallback({
        trade,
        deadline,
        onWallchainDrop: () => null,
    })

    const [{ swapErrorMessage, attemptingTxn, txHash }, setSwapState] = useState<{
        attemptingTxn: boolean
        swapErrorMessage: string | undefined
        txHash: string | undefined
    }>({
        attemptingTxn: false,
        swapErrorMessage: undefined,
        txHash: undefined,
    })

    const { toastWarning } = useToast()

    const handleSwap = useCallback(async () => {
        setSwapState({ attemptingTxn: true, swapErrorMessage: undefined, txHash: undefined })
        const nativeBalance = await publicClient.getBalance({
            address: account,
        })

        if (Number(formatEther(nativeBalance)) < Number(quickBuyAmount)) {
            setSwapState({
                attemptingTxn: false,
                swapErrorMessage: t("Insufficient balance"),
                txHash: undefined,
            })
            toastWarning("Insufficient PLS balance")
            return
        }

        if (!swapCallback) {
            return
        }
        
        try {
            const response = await swapCallback()
            setSwapState({ attemptingTxn: false, swapErrorMessage: undefined, txHash: response.hash })
        } catch (ex) {
            const errorMessage = ex instanceof Error ? ex.message : t('An unknown error occurred. Please try again.')
            setSwapState({
                attemptingTxn: false,
                swapErrorMessage: errorMessage,
                txHash: undefined,
            })
        }
        // return true
    }, [account, publicClient, quickBuyAmount, swapCallback, t, toastWarning])

    useEffect(() => {
        if (!isLoading && trade && !attempted.current) {
            attempted.current = true
            handleSwap()
        }
    }, [handleSwap, isLoading, trade])

    useEffect(() => {
        if (error) {
            toastWarning("Error", error.message)
            setTimeout(() =>  onDismiss?.(), 1_000)
        }
    }, [swapErrorMessage, error, onDismiss, toastWarning])

    return <TransactionConfirmationModal
        title="Confirmation"
        minWidth={['100%', null, '420px']}
        hash={txHash}
        onDismiss={onDismiss}
        attemptingTxn={attemptingTxn}
        content={() => <ConfirmationPendingContent pendingText={'Quick Swap routing...'} />}
        pendingText={
            t("Quick Swap %amount0% %symbol0% for %amount1% %symbol1%", {
                amount0: Number(trade?.inputAmount?.toSignificant(6)),
                symbol0: native?.symbol,
                amount1: Number(trade?.outputAmount?.toSignificant(6)),
                symbol1: currency?.symbol,
            })
        }
    />
}